import { User } from "../components/types/User";
import { whenIdle } from "./whenIdle";

/**
 * GA4 (Google Analytics 4) integration.
 *
 * Mirrors the shape of ./posthog.ts so the rest of the app calls all
 * analytics providers through the same set of verbs (init, capture
 * pageview, identify, reset). The script tag itself is injected from
 * here — NOT from public/index.html — so that:
 *
 *   1. SPA route changes can fire pageviews manually (gtag's default
 *      send_page_view=true only counts the first load, which is why
 *      the GA4 property currently shows 1 user / 30 days while
 *      PostHog sees real traffic).
 *   2. Initialization is a no-op when the env var is missing, matching
 *      PostHog's behavior on a fresh checkout / staging deploy.
 *   3. We can identify users from the same UserContext hook that
 *      already identifies them in PostHog.
 *
 * Measurement ID is wired via REACT_APP_GA_MEASUREMENT_ID (set in
 * bet-juicer/.env and exported from deploy.sh so the production
 * Firebase build inherits it).
 */

type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

let initialized = false;

const gtag = (...args: unknown[]) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
};

export const initGa4 = () => {
  if (initialized || !measurementId || typeof window === "undefined") {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || ((...args: unknown[]) => window.dataLayer!.push(args));

  // send_page_view: false because <Ga4PageviewTracker /> in App.tsx
  // fires one page_view per route change. Letting GA4 auto-fire would
  // duplicate the landing-page hit.
  window.gtag("js", new Date());
  window.gtag("config", measurementId, { send_page_view: false });

  initialized = true;

  // dataLayer holds everything pushed until the script reads it, so pageviews
  // and identify made during the idle wait keep their order.
  whenIdle(() => {
    const tag = document.createElement("script");
    tag.async = true;
    tag.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(tag);
  });
};

export const captureGa4Pageview = (path: string) => {
  if (!initialized || !measurementId) return;
  gtag("event", "page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : path,
    page_title: typeof document !== "undefined" ? document.title : undefined,
  });
};

export const captureGa4Event = (event: string, properties?: AnalyticsProperties) => {
  if (!initialized) return;
  gtag("event", event, properties || {});
};

export const identifyGa4User = (user: User) => {
  if (!initialized || !user?.id || !measurementId) return;
  gtag("config", measurementId, {
    user_id: String(user.id),
    send_page_view: false,
  });
  gtag("set", "user_properties", {
    role: user.role ? String(user.role) : undefined,
    customer_id: user.customer_id || undefined,
  });
};

export const resetGa4 = () => {
  if (!initialized || !measurementId) return;
  gtag("config", measurementId, {
    user_id: null,
    send_page_view: false,
  });
  gtag("set", "user_properties", { role: null, customer_id: null });
};
